import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import Company from "marketplace/companies/company";
import { CompanyJSON } from "marketplace/companies/company-schema";
import { Dispatch, SetStateAction } from "react";
import AuthEmailAPIService, { AuthResponse, ExpiredTokenError } from "users/_api/auth-email-api-service";
import { AccountType } from "users/account-types/account-type";
import { AuthContext } from "users/auth/context/auth-context";
import { AuthContextJSON } from "users/auth/context/auth-context-schema";
import UserCredentials from "users/auth/credentials/user-credentials";
import { currentReturnRoute, goToLogin } from "users/auth/return-routes/login-return-route";
import { clearSignOutInProgress, markSignOutStarted } from "users/__session/sign-out-in-progress";
import { AuthInfo } from "users/auth/info/auth-info";
import UserAuthToken from "users/auth/tokens/user-auth-token";
import { UserAuthTokenJSON } from "users/auth/tokens/user-auth-token-schema";
import UserInformation from "users/information/user-information";
import { User } from "users/user";
import UserSchema, { UserJSON } from "users/user-schema";
import { columnVisibilityStorageKeyPrefix } from "_shared/_views/tables/table-column-visibility-preference";

export interface SessionJSON {
  _user: UserJSON | null;
  _company: CompanyJSON | null;
  _context: AuthContextJSON | null;
  _openEntityAgreements: EntityAgreementJSON[];
  _authToken?: UserAuthTokenJSON;
}

const SESSION_STORAGE_KEY = "Session";

/**
 * The `localStorage` namespace every proposal store writes under, and which logging out clears
 * whole.
 *
 * Declared here as a literal rather than imported from the stores themselves: `proposal-collaboration
 * -local-store.ts` reads `Session` at runtime, so importing it back would close a module cycle. The
 * coupling is pinned instead by `session.spec.ts`, which asserts each store's exported key constant
 * begins with this prefix — so a store that leaves the namespace fails a test rather than quietly
 * outliving its author's session.
 */
export const PROPOSAL_STORAGE_KEY_PREFIX = "attorneyhub.proposals.";
export const SESSION_AUTH_TOKEN_CHANGED_EVENT = "sessionAuthTokenChanged";
/**
 * Raised whenever a login context establishes a session — email login, password-reset
 * confirmation, super-user impersonation, officer- or member-invitation acceptance, and
 * entity-agreement acceptance. The Ably middleware connects off this event, so a login context that
 * writes the session without raising it leaves the app authenticated but disconnected. Every
 * context raises it because every context goes through
 * {@link Session.initializeFromAuthResponse}.
 *
 * The legacy name is kept so nothing that listens for `loggedIn` has to change.
 */
export const SESSION_ESTABLISHED_EVENT = "loggedIn";

class Session {
  public static loadFromStorage(updateSession: Dispatch<SetStateAction<Session | undefined>>): Session {
    const localSessionData = localStorage.getItem(SESSION_STORAGE_KEY);
    const sessionStorageData = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const sessionData = localSessionData ?? sessionStorageData;
    const parsedSessionData = sessionData ? JSON.parse(sessionData) : null;

    if (!parsedSessionData?._authToken?.value) {
      const newSession = new Session();
      newSession._updateSession = updateSession;
      return newSession;
    }

    let session: Session;
    try {
      session = Session.fromJSON(parsedSessionData, updateSession);
      session._rememberSession = !!localSessionData;
    } catch {
      console.error("The saved session could not be loaded.");
      localStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      session = new Session();
      session._updateSession = updateSession;
      return session;
    }

    // Respect token expiry: never rehydrate an expired session. Clear the stale
    // token from storage and drop the auth header so a fresh tab loads logged-out
    // rather than briefly presenting an expired credential.
    if (session._authToken?.isExpired) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      AttorneyHubAPIService.setAuthHeader(null);
      const expiredSession = new Session();
      expiredSession._updateSession = updateSession;
      return expiredSession;
    }

    return session;
  }

  public static fromJSON(
    json: SessionJSON,
    updateSession: Dispatch<SetStateAction<Session | undefined>> | undefined,
  ): Session {
    const session = new Session();
    session._updateSession = updateSession;

    try {
      if (!json._authToken?.value) {
        throw new SessionLoadError("No auth token");
      }
      session._authToken = UserAuthToken.create({
        value: json._authToken.value,
        tokenExp: new AHDate(json._authToken.tokenExp),
      });
      AttorneyHubAPIService.setAuthHeader(session._authToken.value);
    } catch (error) {
      AttorneyHubAPIService.setAuthHeader(null);
      if (error instanceof Error && error.name === "InvalidTokenError") {
        throw new SessionLoadError("Invalid token");
      } else {
        console.error("The saved authentication token could not be parsed.");
        throw new SessionLoadError("Error parsing auth token");
      }
    }

    session._user = new User(UserSchema.deserialize(json._user));
    session._context = json._context ? AuthContext.fromJSON(json._context) : null;
    session._openEntityAgreements = EntityAgreement.fromJSONArray(json._openEntityAgreements) ?? [];

    return session;
  }

  public get user(): User | null {
    return this._user;
  }

  public get context(): AuthContext | null {
    return this._context;
  }

  public get authToken(): UserAuthToken | undefined {
    return this._authToken;
  }

  public get company(): Company | null {
    return this._company;
  }

  public get openEntityAgreements(): EntityAgreement[] {
    return this._openEntityAgreements;
  }

  public get isLoggedIn(): boolean {
    return !!this._user && !!this._authToken && !this._authToken.isExpired;
  }

  public get onboardingWork() {
    return this._user?.onboardingWork;
  }

  public get isRestrictedOnboarding(): boolean {
    const state = this._user?.onboardingWork?.state;
    return !this._user?.isSuperUser && !!state && state !== "Completed";
  }

  public get accountType(): AccountType {
    if (!this._user || !this._context) return AccountType.None;
    return this._context?.viewingAsVendor ? AccountType.Vendor : AccountType.Client;
  }

  public toJSON(): SessionJSON {
    return {
      _user: this._user ? this._user.toJSON() : this._user,
      _company: this._company ? this._company.toJSON() : this._company,
      _context: this._context ? this._context.toJSON() : this._context,
      _openEntityAgreements: this._openEntityAgreements?.map((agreement) => agreement.toJSON()) ?? [],
      _authToken: this._authToken ? (this._authToken.toJSON() ?? undefined) : undefined,
    };
  }

  public login = async (userCredentials: UserCredentials, remember: boolean) => {
    const authResponse: AuthResponse = await AuthEmailAPIService.login(userCredentials, remember);

    this.initializeFromAuthResponse(authResponse, remember);
    return authResponse;
  };

  /**
   * Ends the session and goes to the login screen.
   *
   * `returnTo` is the route to come back to once the person signs in again, and only a stale
   * session passes one: a token that expired while the tab sat open, a 401 from the API, or an Ably
   * credential the server refused. Signing out on purpose passes nothing, because somebody who
   * chose to leave a page should not be put back on it by their next sign-in.
   */
  public logout = async (returnTo?: string) => {
    // First, before the credential goes: a request answered from here on was orphaned by this
    // sign-out, and its failure is not news to anyone.
    markSignOutStarted();
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      // Everything a proposal keeps in this browser goes, by namespace rather than by name.
      //
      // This used to name each key: the local store, the collaboration queue, and two legacy keys
      // nothing writes any more. `attorneyhub.proposals.uncommittedInput.v1.*` — a comment composer's
      // typed-but-unposted text and a reviewer selector's staged, unsent selection — was added later
      // and never joined the list, so an unsent private comment stayed readable in the browser after
      // its author logged out, on a machine the next person signs into.
      //
      // A hand-maintained list of keys is the defect, not the missing entry: a store added under this
      // namespace persists user work by definition, and nothing makes its author update a list in
      // another business center. Removing the whole prefix means the next such store is covered on
      // the day it is written.
      for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index);
        // Each table's copy of the reader's column choice goes too: the account holds the choice, and a
        // copy left here would open the next reader's tables with these columns until the server
        // answered (#2788).
        if (key?.startsWith(PROPOSAL_STORAGE_KEY_PREFIX) || key?.startsWith(columnVisibilityStorageKeyPrefix)) {
          localStorage.removeItem(key);
        }
      }
      localStorage.removeItem("proposalDrafts");
      localStorage.removeItem("work.proposals.reviewerSelector.infoDismissed");
    } catch {
      // Ignore storage failures
    }
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage failures
    }
    this._user = null;
    this._company = null;
    this._context = null;
    this._openEntityAgreements = [];
    this._authToken = undefined;
    AttorneyHubAPIService.setAuthHeader(null);
    document.dispatchEvent(new CustomEvent("loggedOut"));
    goToLogin(returnTo);
  };

  /**
   * Refreshes identity metadata without replacing the selected account view.
   * Account view changes are owned by setAccountViewType and persisted before
   * the user-information query can refresh, so that query may legitimately be
   * one response behind the current session.
   */
  public refreshFromUserInfo(userInfo: UserInformation) {
    const sessionBeforeRefresh = JSON.stringify(this.toJSON());
    this._user = User.create({
      name: userInfo.name,
      email: userInfo.email ?? EmailAddress.create(""),
      id: userInfo.id,
      isSuperUser: userInfo.isSuperUser,
      hasSigningAuthority: userInfo.hasSigningAuthority,
      // The two grants that decide which entity-wide Ably channels this session asks for. Dropped
      // here, a refresh after login took the channels away until the next sign-in (#6785).
      hasEntityWideNotificationsAccess: userInfo.hasEntityWideNotificationsAccess,
      hasMemberPermissionsAccess: userInfo.hasMemberPermissionsAccess,
      individualId: userInfo.individualId,
      legalEntityId: userInfo.legalEntityId,
      companyName: userInfo.companyName,
      onboardingWork: userInfo.onboardingWork,
    });
    this._openEntityAgreements = userInfo.openEntityAgreements.map((agreement) =>
      EntityAgreement.fromUserOpenEntityAgreement(agreement),
    );
    const refreshedSessionJson = this.toJSON();
    if (JSON.stringify(refreshedSessionJson) === sessionBeforeRefresh) return;

    this.persist();

    // User-information polling can change the onboarding authorization state.
    // Publish a new Session reference so React immediately re-evaluates route
    // guards, polling, and the restricted shell after promotion completes.
    if (this._updateSession) {
      const updatedSession = Session.fromJSON(refreshedSessionJson, this._updateSession);
      updatedSession._rememberSession = this._rememberSession;
      this._updateSession(updatedSession);
    }
  }

  /**
   * Parses a login response and establishes the session from it.
   *
   * Every login context posts to a different endpoint, but all of them return the one response
   * contract (`{ authToken, userInformation }`), so all of them parse it here rather than each
   * reaching for the schema itself.
   */
  public initializeFromAuthPayload(payload: unknown, remember: boolean = true): AuthResponse {
    const authResponse = AuthInfo.parseAuthResponse(payload);
    this.initializeFromAuthResponse(authResponse, remember);
    return authResponse;
  }

  /**
   * The single way a session is established. Email login, password-reset confirmation, super-user
   * impersonation, officer- and member-invitation acceptance, and entity-agreement acceptance all
   * end here, so all of them persist the session, set the API auth header, hand React a fresh
   * session, and raise {@link SESSION_ESTABLISHED_EVENT} — which is what starts the Ably
   * connection. A context that did any of this itself would inevitably miss a step.
   */
  public initializeFromAuthResponse(authResponse: AuthResponse, remember: boolean = true) {
    // A document can outlive its sign-out (a leave-page prompt answered "Stay") and sign in again
    // through the router. Its failures are news again from here on.
    clearSignOutInProgress();
    this._rememberSession = remember;
    this._user = authResponse.user;
    this._context = authResponse.context;
    this._authToken = authResponse.authToken;
    this._openEntityAgreements = authResponse.openEntityAgreements.map((agreement) =>
      EntityAgreement.fromUserOpenEntityAgreement(agreement),
    );
    this.persist();
    AttorneyHubAPIService.setAuthHeader(this._authToken?.value);
    this.publishToReact();
    document.dispatchEvent(new CustomEvent(SESSION_ESTABLISHED_EVENT));
  }

  public async setAccountViewType(accountType: AccountType) {
    if (this.isRestrictedOnboarding) {
      console.warn("Account view cannot change during onboarding.");
      return;
    }
    if (!this._updateSession) {
      console.warn("Cannot update session.");
    }
    if (!this.context) return;

    const viewingAsVendor = accountType === AccountType.Vendor;
    const alreadyInThisView = Boolean(this.context.viewingAsVendor) === viewingAsVendor;
    const newContext = AuthContext.create({ viewingAsVendor });

    await this.setContext(newContext);

    // A view that did not move has nothing to republish, and republishing it anyway is not
    // harmless: `SessionProvider` calls this on mount purely to tell the server which view the
    // session was left in, and that call takes a round trip. Rebuilding a session from the state
    // this one held when the call STARTED, and publishing it a round trip later, overwrites
    // whatever the person did in between -- switching view from the account menu among it.
    if (alreadyInThisView) return;

    const updatedSession = Session.fromJSON(this.toJSON(), this._updateSession);
    updatedSession._rememberSession = this._rememberSession;
    this._updateSession?.(updatedSession);
    updatedSession.persist();
  }

  public async setContext(newContext: AuthContext) {
    if (!this._context) return;
    this._context = newContext;
    this.persist();
    if (this.isLoggedIn) {
      try {
        await AuthEmailAPIService.updateUserVendorView(this._context.viewingAsVendor ?? false, this.authToken);
      } catch (error) {
        // This is the first request a restored session makes, so it is usually the first thing to
        // learn that the server no longer accepts the token. It signed the person out already; it
        // dropped the route on the floor, which is what put them on the dashboard afterwards.
        if (error instanceof ExpiredTokenError) await this.logout(currentReturnRoute());
        else throw error;
      }
    }
  }

  public updateAuthToken = (value?: UserAuthToken) => {
    this._authToken = value;
    this.persist();
    AttorneyHubAPIService.setAuthHeader(value?.value ?? null);
    this._updateSession?.(this);
    this.dispatchAuthTokenChanged();
  };

  private _user: User | null = null;
  private _company: Company | null = null;
  private _context: AuthContext | null = null;
  private _openEntityAgreements: EntityAgreement[] = [];
  private _authToken?: UserAuthToken;
  private _rememberSession = true;
  private _updateSession: Dispatch<SetStateAction<Session | undefined>> | undefined;

  /**
   * Hands React a session instance it will treat as changed. Passing `this` after mutating it in
   * place is a no-op for `useState`, which compares by reference, so consumers of the session
   * context would keep rendering the signed-out session.
   */
  private publishToReact() {
    if (!this._updateSession) return;
    try {
      const republished = Session.fromJSON(this.toJSON(), this._updateSession);
      republished._rememberSession = this._rememberSession;
      this._updateSession(republished);
    } catch {
      console.error("The session could not be republished to React.");
      this._updateSession(this);
    }
  }

  private persist() {
    // The auth session must live in the durable, cross-tab store (localStorage)
    // so that opening the app in a new browser tab restores the session instead
    // of logging the user out. sessionStorage is scoped to a single tab and is
    // always empty in a freshly-opened tab, which caused the logout regression.
    // Any legacy sessionStorage copy is cleared so the durable copy stays the
    // single source of truth. Token expiry is enforced in loadFromStorage.
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(this.toJSON()));
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }

  private dispatchAuthTokenChanged() {
    document.dispatchEvent(new CustomEvent(SESSION_AUTH_TOKEN_CHANGED_EVENT));
  }
}

export default Session;

export class SessionLoadError extends Error {}
